/**
 * P3-38: UseHeatBtn —— 用 chrono furnace heat 粉碎时间水晶。
 *
 * 对齐 `js/time.js:1478-1618`：
 * - 主按钮点击消耗 100 heat 粉碎 1 年
 * - useAllLink：尽可能多（至少 2）
 * - customLink：基于 shatterYearBoost
 * - useAllLink 始终 `rightestLink`
 */
import { useMemo, type MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { GameButton } from "../../components/GameButton";
import type { GameButtonLinkModel } from "../../components/GameButton";
import { useButtonModel, makeModernTooltip, type ModernButtonController } from "../bonfire/useButtonModel";

interface HeatLinkSnapshot {
  title: string;
  quantity: number | string;
  visible: boolean;
}

interface UseHeatSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  useAll: HeatLinkSnapshot;
  custom: HeatLinkSnapshot | null;
}

function toLink(toggle: Record<string, unknown> | undefined): HeatLinkSnapshot | null {
  if (!toggle) {
    return null;
  }
  return {
    title: String(toggle.title ?? ""),
    quantity: (toggle.quantity as number | string) ?? 0,
    visible: Boolean(toggle.visible),
  };
}

function toSnapshot(model: Record<string, unknown>): UseHeatSnapshot {
  return {
    name: String(model.name ?? ""),
    description: String(model.description ?? ""),
    visible: Boolean(model.visible),
    enabled: Boolean(model.enabled),
    useAll: toLink(model.useAllLink as Record<string, unknown>) ?? { title: "", quantity: 0, visible: false },
    custom: toLink(model.customLink as Record<string, unknown>),
  };
}

function snapEq(a: UseHeatSnapshot | null, b: UseHeatSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (
    a.name !== b.name ||
    a.description !== b.description ||
    a.visible !== b.visible ||
    a.enabled !== b.enabled ||
    a.useAll.title !== b.useAll.title ||
    a.useAll.quantity !== b.useAll.quantity ||
    a.useAll.visible !== b.useAll.visible
  ) {
    return false;
  }
  if ((a.custom === null) !== (b.custom === null)) {
    return false;
  }
  if (a.custom && b.custom) {
    if (
      a.custom.title !== b.custom.title ||
      a.custom.quantity !== b.custom.quantity ||
      a.custom.visible !== b.custom.visible
    ) {
      return false;
    }
  }
  return true;
}

const CONTROLLER_OPTS = { name: "time.use.heat", description: "time.use.heat.desc" };

export default function UseHeatBtn() {
  const controller = useMemo<ModernButtonController | null>(() => {
    const ControllerClass = (
      window as unknown as {
        classes?: { ui?: { time?: { UseHeatBtnController?: new (game: GamePage) => ModernButtonController } } };
      }
    ).classes?.ui?.time?.UseHeatBtnController;
    const game = getGame();
    if (typeof ControllerClass !== "function" || !game) {
      return null;
    }
    return new ControllerClass(game);
  }, []);

  const { snap, modelRef } = useButtonModel(controller, () => CONTROLLER_OPTS, toSnapshot, snapEq);

  const getTooltip = makeModernTooltip(() => controller, () => CONTROLLER_OPTS);

  if (!snap || !snap.visible) {
    return null;
  }

  const getModel = () => modelRef.current;

  const custom = snap.custom;
  const links: GameButtonLinkModel[] = [
    {
      title: snap.useAll.title,
      cssClass: "rightestLink",
      visible: snap.useAll.visible,
      handler: () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (controller as any)?.doShatterAmt?.(getModel(), snap.useAll.quantity);
        getGame()?.render();
      },
    },
    ...(custom
      ? [
          {
            title: custom.title,
            visible: custom.visible,
            handler: () => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (controller as any)?.doShatterAmt?.(getModel(), custom.quantity);
              getGame()?.render();
            },
          },
        ]
      : []),
  ];

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      enabled={snap.enabled}
      getTooltip={getTooltip}
      onClick={(event: MouseEvent) => {
        controller?.buyItem?.(modelRef.current, event);
        getGame()?.render();
      }}
      links={links}
    />
  );
}
